

import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Stack, Skeleton, lighten } from '@mui/material';
import TraceDetails from './TraceDetails'; // Import the newly modified TraceDetails component

// --- Color Logic ---
// const PREDEFINED_PALETTE = [
//   '#0064FF', // 1st trace
//   '#FBBC05', // 2nd trace
//   '#BF83FF', // 3rd trace
//   '#3CD856', // 4th trace
//   '#FF947A', // 5th trace
//   '#FA5A7D', // 6th trace
// ];
const PREDEFINED_PALETTE = [
  '#0064FF', '#FFA600', '#FF3023', '#67B341', '#029F8C', '#9B2B11', '#E75D0F', '#AE46BC',
  '#180273','#5B733D', '#6C63C8', '#8C8C8C'
];

const stringToColor = (str: string): string => {
  let hash = 0;
  str.split('').forEach(char => {
    hash = char.charCodeAt(0) + ((hash << 5) - hash);
  });
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

// --- Interfaces ---
interface TraceSplitData {
  count: number;
  name: string;
}

interface TracesProps {
  traceSplitData?: TraceSplitData[];
  totalTraces?: number;
  isLoading?: boolean;
  tenantId?: string; // Add tenantId to pass to the panel
}

const Traces: React.FC<TracesProps> = ({ traceSplitData = [], totalTraces = 0, isLoading, tenantId }) => {
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const maxValue = Math.max(...traceSplitData.map(d => d.count), 0);

    const handleViewDetailsClick = () => {
        setIsPanelOpen(true);
    };

    const handlePanelClose = () => {
        setIsPanelOpen(false);
    };

    return (
        <>
            <Card elevation={0} sx={{ borderRadius: '16px', height: '100%', fontFamily: 'Inter' }}>
                <CardContent sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {isLoading ? (
                        <>
                            <Skeleton variant="text" width="80px" height={24} />
                            <Skeleton variant="text" width="200px" height={40} sx={{ mt: 2, mb: 1 }} />
                        </>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6" component="h3" sx={{ fontWeight: '600', fontSize: '16px', fontFamily: 'Inter' }}>
                                    Traces
                                </Typography>
                                <Typography
                                    variant="body2"
                                    onClick={handleViewDetailsClick}
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        fontFamily: 'Inter',
                                        color: '#0064FF',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        }
                                    }}
                                >
                                    View Details
                                </Typography>
                            </Box>
                            <Typography variant="h4" component="p" sx={{ fontWeight: '600', mt: 2, mb: 1, fontFamily: 'Inter' }}>
                                {totalTraces} <Typography component="span" sx={{ color: 'text.secondary', fontSize: '1rem', fontWeight: 'normal', fontFamily: 'Inter' }}>Total traces tracked</Typography>
                            </Typography>
                        </>
                    )}

                    <Stack spacing={2.5} sx={{ mt: 3, flexGrow: 1 }}>
                        {isLoading ? (
                            <>
                                <Skeleton variant="rectangular" sx={{ borderRadius: '6px', height: '48px' }} />
                                <Skeleton variant="rectangular" sx={{ borderRadius: '6px', height: '48px' }} />
                            </>
                        ) : (
                            traceSplitData.map((item, index) => {
                                const barWidthPercent = maxValue > 0 ? (item.count / maxValue) * 100 : 0;
                                const color = index < PREDEFINED_PALETTE.length
                                  ? PREDEFINED_PALETTE[index]
                                  : stringToColor(item.name);
                                const gradient = `linear-gradient(to right, ${lighten(color, 0.5)}, ${color})`;

                                return (
                                    <Box key={item.name} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Box
                                                sx={{
                                                    background: gradient,
                                                    borderRadius: '6px',
                                                    p: '12px 12px',
                                                    width: `${barWidthPercent}%`,
                                                    minWidth: 'fit-content',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: '#333' }}>
                                                    {item.name}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: '#333', fontWeight: 500, pl: 2 }}>
                                            {item.count}
                                        </Typography>
                                    </Box>
                                );
                            })
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {/* Render the TraceDetails component as a panel, controlled by state */}
            <TraceDetails
                open={isPanelOpen}
                onClose={handlePanelClose}
                tenantId={tenantId}
            />
        </>
    );
};

export default Traces;